import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useMe } from "./lib/queries";
import { UploadProvider } from "./lib/uploadQueue";
import { AppShell } from "./components/AppShell";
import { ErrorState } from "./components/ErrorState";
import { CollectionCardSkeleton, LoadingAnnouncement } from "./components/Skeleton";
import { ToastProvider, Toasts } from "./components/Toasts";
import { CollectionDetail } from "./routes/CollectionDetail";
import { Collections } from "./routes/Collections";
import { EntryDetail } from "./routes/EntryDetail";
import { Landing } from "./routes/Landing";
import { NotFound } from "./routes/NotFound";
import { Scrapbook } from "./routes/Scrapbook";

export default function App() {
  return (
    // Declarative BrowserRouter, not the framework/data mode: the API is the
    // source of truth, so there are no loaders to hang routes off.
    <BrowserRouter>
      <ToastProvider>
        <UploadProvider>
          <Routes>
            <Route path="/" element={<Entrance />} />

            <Route
              path="/scrapbook"
              element={
                <RequireAuth>
                  <Scrapbook />
                </RequireAuth>
              }
            />
            <Route
              path="/entry/:entryId"
              element={
                <RequireAuth>
                  <EntryDetail />
                </RequireAuth>
              }
            />
            <Route
              path="/collections"
              element={
                <RequireAuth>
                  <Collections />
                </RequireAuth>
              }
            />
            <Route
              path="/collections/:collectionId"
              element={
                <RequireAuth>
                  <CollectionDetail />
                </RequireAuth>
              }
            />

            <Route
              path="*"
              element={
                <RequireAuth>
                  <NotFound />
                </RequireAuth>
              }
            />
          </Routes>
          <Toasts />
        </UploadProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

/**
 * `/` is the title page when you're a stranger and the scrapbook when you're
 * not.
 */
function Entrance() {
  const { me, isUnauthenticated, isLoading, error, refetch } = useMe();

  if (isLoading) return <BootingPage />;
  // A 401 here is the ordinary state of a first visit, not a failure.
  if (isUnauthenticated || (!me && !error)) return <Landing />;
  if (error) return <ApiDown error={error} onRetry={refetch} />;
  return <Navigate to="/scrapbook" replace />;
}

/**
 * The gate. A 401 sends you to the title page, but remembers where you were
 * going so signing in doesn't lose your place.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { me, isUnauthenticated, isLoading, error, refetch } = useMe();

  if (isLoading) return <BootingPage />;

  if (isUnauthenticated || (!me && !error)) {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/" replace state={{ from }} />;
  }

  if (error || !me) return <ApiDown error={error} onRetry={refetch} />;

  return <AppShell me={me}>{children}</AppShell>;
}

/** Opening the book: paper-toned, never a spinner. */
function BootingPage() {
  return (
    <div className="grain relative min-h-dvh">
      <div className="mx-auto max-w-5xl px-safe py-10">
        <LoadingAnnouncement>Opening Foundling…</LoadingAnnouncement>
        <div className="hairline mb-8" />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <CollectionCardSkeleton key={index} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Not a 401 — something is actually wrong with the API. */
function ApiDown({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="grain relative flex min-h-dvh items-center justify-center px-safe py-10">
      <ErrorState
        error={error}
        title="Foundling can't reach its own records."
        onRetry={onRetry}
        retryLabel="Try again"
      />
    </div>
  );
}
